import { createMachine, spawn } from 'xstate';
import { assign } from 'xstate/lib/actions';

import { getAPIErrorResponse } from '@nw/shared/utils';

import {
  CLEAR_OTP_ERROR,
  OTP_FIELD_INITIAL_STATE,
  OTP_FIELD_MACHINE_ID,
  OTP_LOADING,
  OTP_VERIFIED_STATE,
  otpFieldMachineActions,
  RESEND_OTP_CLICKED,
  RESEND_OTP_CLICKED_STATE,
  RESEND_OTP_FAILED_STATE,
  RESEND_OTP_THROUGH_CALL_CLICKED,
  RESEND_OTP_THROUGH_CALL_CLICKED_STATE,
  RESENDING_OTP_TAG,
  VERIFY_OTP_BUTTON_CLICKED,
  VERIFY_OTP_BUTTON_CLICKED_STATE,
  VERIFY_OTP_FAILED_STATE
} from '../../constants/otpFieldMachineConstants';
import { otpStateMachineEvents } from '../../constants/otpStateMachineConstants';

import { otpStateMachine } from '../OtpStateMachine';

export type OTPFieldEvent =
  | { type: 'VERIFY_OTP_BUTTON_CLICKED'; values: any; next?: () => void }
  | { type: 'RESEND_ENABLE' }
  | { type: 'UPDATE_PHONE_NUMBER' }
  | { type: 'RESEND_OTP_CLICKED' }
  | { type: 'RESEND_DISABLE' }
  | { type: 'RESEND_OTP_THROUGH_CALL_CLICKED' }
  | { type: 'CLEAR_OTP_ERROR' };

const actions = otpFieldMachineActions;

export interface OTPFieldContext {
  shouldShowResendAndTimerField: boolean;
  errorMessage: string;
  shouldSendOTPThroughCall: boolean;
  next?: () => void;
  verificationOTPError?: string;
}

export const otpFieldMachine = () =>
  createMachine<OTPFieldContext, OTPFieldEvent>(
    {
      id: OTP_FIELD_MACHINE_ID,
      initial: OTP_FIELD_INITIAL_STATE,
      context: {
        shouldShowResendAndTimerField: true,
        errorMessage: '',
        shouldSendOTPThroughCall: false
      },
      on: {
        [RESEND_OTP_CLICKED]: {
          actions: [actions.assignResetTimerToContext, actions.clearErrorMessage],
          target: RESEND_OTP_CLICKED_STATE
        },
        [RESEND_OTP_THROUGH_CALL_CLICKED]: {
          actions: [actions.assignResetTimerToContext],
          target: RESEND_OTP_THROUGH_CALL_CLICKED_STATE
        },
        [VERIFY_OTP_BUTTON_CLICKED]: VERIFY_OTP_BUTTON_CLICKED_STATE,
        [CLEAR_OTP_ERROR]: {
          actions: [actions.clearErrorMessage]
        }
      },
      entry: [actions.spawnOTPStateMachine],
      states: {
        [OTP_FIELD_INITIAL_STATE]: {
          on: {
            [RESEND_OTP_CLICKED]: {
              actions: [actions.assignResetTimerToContext, actions.clearErrorMessage],
              target: RESEND_OTP_CLICKED_STATE
            },
            [RESEND_OTP_THROUGH_CALL_CLICKED]: {
              actions: [actions.assignResetTimerToContext],
              target: RESEND_OTP_THROUGH_CALL_CLICKED_STATE
            },
            [VERIFY_OTP_BUTTON_CLICKED]: VERIFY_OTP_BUTTON_CLICKED_STATE
          }
        },
        [RESEND_OTP_CLICKED_STATE]: {
          entry: [
            actions.setSendOTPThroughAsSMS,
            actions.sendResendOtpEventToDataLayer,
            actions.onClickResendOtp
          ],
          tags: [RESENDING_OTP_TAG],
          invoke: {
            src: 'resendOTP',
            onDone: {
              actions: [actions.sendToDisableState, actions.resetTimer],
              target: OTP_FIELD_INITIAL_STATE
            },
            onError: {
              actions: [actions.sendToEnableState, actions.showToastMessage],
              target: RESEND_OTP_FAILED_STATE
            }
          }
        },
        [RESEND_OTP_THROUGH_CALL_CLICKED_STATE]: {
          entry: [actions.setSendOTPThroughAsCall],
          tags: [RESENDING_OTP_TAG],
          invoke: {
            src: 'resendOTP',
            onDone: {
              actions: [actions.sendToDisableState, actions.resetTimer],
              target: OTP_FIELD_INITIAL_STATE
            },
            onError: {
              actions: [
                actions.sendToEnableState,
                actions.assignErrorMessage,
                actions.showToastMessage
              ],
              target: RESEND_OTP_FAILED_STATE
            }
          }
        },
        [VERIFY_OTP_BUTTON_CLICKED_STATE]: {
          entry: [
            actions.sendMultiStepFormNextToContext,
            actions.clearErrorMessage
          ],
          tags: [OTP_LOADING],
          invoke: {
            src: 'verifyOTP',
            onDone: {
              actions: [
                actions.hideResendAndTimerField,
                actions.setAccessTokenAndRefreshTokenAndSendParent
              ],
              target: OTP_VERIFIED_STATE
            },

            onError: {
              target: VERIFY_OTP_FAILED_STATE
            }
          }
        },
        [VERIFY_OTP_FAILED_STATE]: {
          entry: [actions.assignErrorMessage, actions.showOTPVerifyFailedToast],
          on: {
            [VERIFY_OTP_BUTTON_CLICKED]: VERIFY_OTP_BUTTON_CLICKED_STATE,
            [CLEAR_OTP_ERROR]: {
              actions: [actions.clearErrorMessage]
            }
          }
        },
        [RESEND_OTP_FAILED_STATE]: {
          on: {
            [RESEND_OTP_CLICKED]: {
              actions: [actions.assignResetTimerToContext, actions.clearErrorMessage],
              target: RESEND_OTP_CLICKED_STATE
            },
            [RESEND_OTP_THROUGH_CALL_CLICKED]: {
              actions: [actions.assignResetTimerToContext],
              target: RESEND_OTP_THROUGH_CALL_CLICKED_STATE
            }
          }
        },
        [OTP_VERIFIED_STATE]: {
          type: 'final'
        }
      }
    },
    {
      actions: {
        assignErrorMessage: assign((_context, event: any) => ({
          errorMessage: getAPIErrorResponse(event.data)
        })),
        clearErrorMessage: assign((_context, _event: any) => ({
          errorMessage: ''
        })),
        setSendOTPThroughAsCall: assign((_context) => ({
          shouldSendOTPThroughCall: true
        })),
        setSendOTPThroughAsSMS: assign((_context) => ({
          shouldSendOTPThroughCall: false
        })),
        sendToDisableState: assign((context: any, _event: any) => {
          context.otpStateRef.send(otpStateMachineEvents.RESEND_DISABLE);
        }),
        sendToEnableState: assign((context: any, _event: any) => {
          context.otpStateRef.send(otpStateMachineEvents.RESEND_ENABLE);
        }),
        assignResetTimerToContext: assign((context: any, event: any) => {
          return {
            resetTimer: event.resetTimer
          };
        }),
        resetTimer: (context: any, event: any) => {
          context.resetTimer && context.resetTimer();
        },
        spawnOTPStateMachine: assign((context: any, _event: any) => ({
          otpStateRef: context.otpStateRef
            ? context.otpStateRef
            : spawn(otpStateMachine())
        }))
      }
    }
  );
